import { useCallback, useEffect, useState } from 'react';
import { apiUrl } from '../config/api';
import Pagination, { DEFAULT_PAGE_SIZE, pagingParams } from '../components/Pagination';
import { apiFetch, getSessionStatus } from '../utils/session';
import './HelpRequest.css';
import './Community.css';

function statusAttr(status) {
  if (status === 'Hidden') return 'Closed';
  if (status === 'Open') return 'Resolved';
  return 'New';
}

function BusinessCard({ business, busy, onContact, onUpdate }) {
  return (
    <article className="help-card">
      <span className="help-status" data-status={statusAttr(business.status)}>
        {business.status}
      </span>
      <h2 className="help-card-title">{business.name}</h2>
      <p className="help-meta">
        {business.serviceType} · {business.area}
        {business.ownerName ? ` · ${business.ownerName}` : ''}
        {business.ownerMemberId ? ` · ${business.ownerMemberId}` : ''}
      </p>
      <p className="help-body">{business.description}</p>
      {business.phone ? (
        <p className="help-meta">Phone: {business.phone}</p>
      ) : (
        <p className="help-hint">Phone number stays with the Area Head until they accept a contact request.</p>
      )}
      {business.myContact ? <p className="help-meta">Contact request sent to the Area Head.</p> : null}
      {business.status === 'Open' && !business.isOwner && !business.myContact ? (
        <button type="button" className="help-btn primary" disabled={busy} onClick={() => onContact(business._id)}>
          Contact
        </button>
      ) : null}
      {business.canManage && business.status === 'Pending approval' ? (
        <button
          type="button"
          className="help-btn primary"
          disabled={busy}
          onClick={() => onUpdate(business._id, { status: 'Open' })}
        >
          Approve
        </button>
      ) : null}
      {business.canHide && business.status !== 'Hidden' ? (
        <button
          type="button"
          className="help-btn"
          disabled={busy}
          onClick={() => onUpdate(business._id, { status: 'Hidden' })}
        >
          Hide
        </button>
      ) : null}
      {business.isOwner ? (
        <label className="help-hint">
          <input
            type="checkbox"
            checked={Boolean(business.showPhone)}
            disabled={busy}
            onChange={(e) => onUpdate(business._id, { showPhone: e.target.checked })}
          />{' '}
          Show my phone on this card
        </label>
      ) : null}
    </article>
  );
}

export default function LocalBusiness() {
  const [businesses, setBusinesses] = useState([]);
  const [area, setArea] = useState('');
  const [name, setName] = useState('');
  const [serviceType, setServiceType] = useState('');
  const [description, setDescription] = useState('');
  const [showPhone, setShowPhone] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const role = sessionStorage.getItem('sangramUserRole');
  const approved = getSessionStatus() === 'Active';

  const load = useCallback(async () => {
    setError('');
    const res = await apiFetch(apiUrl(`/businesses?${pagingParams(page, pageSize)}`));
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Could not load businesses');
    setArea(data.area || '');
    setBusinesses(data.businesses || []);
    setTotal(data.total ?? (data.businesses || []).length);
  }, [page, pageSize]);

  useEffect(() => {
    if (!approved) {
      setLoading(false);
      return;
    }
    load()
      .catch((e) => setError(e.message || 'Could not load businesses'))
      .finally(() => setLoading(false));
  }, [approved, load]);

  const createListing = async (e) => {
    e.preventDefault();
    setBusyKey('create');
    setError('');
    setNotice('');
    try {
      const res = await apiFetch(apiUrl('/businesses'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, serviceType, description, showPhone }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Could not list this business');
        return;
      }
      setName('');
      setServiceType('');
      setDescription('');
      setShowPhone(false);
      setNotice(
        data.business?.status === 'Pending approval'
          ? 'Listed. It stays hidden until your Area Head approves it.'
          : 'Listed for your area.'
      );
      setPage(1);
      await load();
    } catch {
      setError('Network error. Try again.');
    } finally {
      setBusyKey('');
    }
  };

  const updateListing = async (id, body) => {
    setBusyKey(id);
    setError('');
    setNotice('');
    try {
      const res = await apiFetch(apiUrl(`/businesses/${id}`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Could not update this business');
        return;
      }
      await load();
    } catch {
      setError('Network error. Try again.');
    } finally {
      setBusyKey('');
    }
  };

  const contact = async (id) => {
    setBusyKey(id);
    setError('');
    setNotice('');
    try {
      const res = await apiFetch(apiUrl(`/businesses/${id}/contact`), { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Could not contact this business');
        return;
      }
      setNotice('Contact sent to the Area Head. The phone number stays off this card until they accept.');
      await load();
    } catch {
      setError('Network error. Try again.');
    } finally {
      setBusyKey('');
    }
  };

  return (
    <div className="help-page">
      <div className="help-shell">
        <header className="help-header">
          <div>
            <h1 className="help-title">Local business</h1>
            <p className="help-sub">
              {area
                ? `Services listed in ${area}. Contact goes to the Area Head first.`
                : 'Services listed in your area. Contact goes to the Area Head first.'}
            </p>
          </div>
        </header>

        {!approved ? (
          <p className="help-error" role="alert">
            Local business is available after your membership is approved.
          </p>
        ) : null}
        {error ? (
          <p className="help-error" role="alert">
            {error}
          </p>
        ) : null}
        {notice ? <p className="help-notice">{notice}</p> : null}

        {approved ? (
          <form className="help-form" onSubmit={createListing}>
            <h2 className="help-card-title">List a service</h2>
            <label className="help-field">
              Business name
              <input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} required />
            </label>
            <label className="help-field">
              Service type
              <input value={serviceType} onChange={(e) => setServiceType(e.target.value)} maxLength={80} required />
            </label>
            <label className="help-field">
              What do you offer?
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} maxLength={500} required />
            </label>
            <p className="help-hint">
              {role === 'Area Head' || role === 'Admin'
                ? `This listing goes live in ${area || 'your area'}.`
                : `This listing is for ${area || 'your area'} and stays hidden until the Area Head approves it.`}
            </p>
            <label className="help-hint">
              <input type="checkbox" checked={showPhone} onChange={(e) => setShowPhone(e.target.checked)} /> Show my phone on the card
            </label>
            <button type="submit" className="help-btn primary" disabled={Boolean(busyKey)}>
              {busyKey === 'create' ? 'Saving…' : 'List service'}
            </button>
          </form>
        ) : null}

        {loading ? <p className="help-muted">Loading…</p> : null}
        {approved && !loading && businesses.length === 0 ? (
          <p className="help-muted">No services in your area yet.</p>
        ) : null}
        <ul className="help-list">
          {businesses.map((business) => (
            <li key={business._id}>
              <BusinessCard business={business} busy={Boolean(busyKey)} onContact={contact} onUpdate={updateListing} />
            </li>
          ))}
        </ul>
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
        />
      </div>
    </div>
  );
}
